import { CheckIcon, PencilIcon, XIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

import { getCssVariableHexColor, HEX_COLOR_PATTERN, normalizeHexColor } from './utils/color';

const COLOR_PICKER_FALLBACK = '#FFFFFF';

type ColorCellProps = {
  value: string | null;
  fallbackCssVariable: string;
  onChange: (value: string | null) => void;
};

export const CustomerIdentityColorCell = ({
  value,
  fallbackCssVariable,
  onChange,
}: ColorCellProps) => {
  const { t } = useTranslation();
  const [draftColor, setDraftColor] = useState(value ?? '');
  const [isEditing, setIsEditing] = useState(false);
  const [fallbackColor, setFallbackColor] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setFallbackColor(getCssVariableHexColor(fallbackCssVariable));
  }, [fallbackCssVariable]);

  useEffect(() => {
    if (!isEditing) {
      setDraftColor(value ?? '');
    }
  }, [value, isEditing]);

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [isEditing]);

  const normalizedDraftColor = normalizeHexColor(draftColor);
  const isDraftEmpty = draftColor.trim() === '';
  const isDraftValid = HEX_COLOR_PATTERN.test(normalizedDraftColor);
  const pickerColor = isDraftValid
    ? normalizedDraftColor
    : (fallbackColor ?? COLOR_PICKER_FALLBACK);
  const displayedLabel = value ?? '-';

  const startEditing = () => {
    setDraftColor(value ?? '');
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setDraftColor(value ?? '');
    setIsEditing(false);
  };

  const confirmEditing = () => {
    if (isDraftEmpty) {
      onChange(null);
      setDraftColor('');
      setIsEditing(false);
      return;
    }

    if (!isDraftValid) return;

    onChange(normalizedDraftColor);
    setDraftColor(normalizedDraftColor);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className="flex min-w-0 max-w-full items-center gap-0">
        <label className="relative flex size-6 shrink-0 cursor-pointer overflow-hidden rounded-sm border border-background-darker shadow-sm 2xl:size-7">
          <span
            aria-hidden="true"
            className="absolute inset-0"
            style={{
              backgroundColor:
                isDraftValid && !isDraftEmpty ? normalizedDraftColor : (fallbackColor ?? undefined),
            }}
          />
          <input
            type="color"
            aria-label={t('pickColor')}
            value={pickerColor}
            className="absolute inset-0 cursor-pointer opacity-0"
            onChange={(event) => setDraftColor(event.target.value.toUpperCase())}
          />
        </label>
        <input
          ref={inputRef}
          type="text"
          aria-label={t('hexColorInput')}
          value={draftColor}
          className={cn(
            'h-8 w-[5.25rem] shrink-0 rounded-sm border-2 bg-white px-1 font-mono text-xs font-semibold uppercase leading-5 text-primary-darker shadow-sm outline-none 2xl:h-9 2xl:w-24 2xl:px-2',
            isDraftValid || isDraftEmpty ? 'border-primary-light' : 'border-danger'
          )}
          onChange={(event) => setDraftColor(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') confirmEditing();
            if (event.key === 'Escape') cancelEditing();
          }}
        />
        <button
          type="button"
          aria-label={t('applyColor')}
          disabled={!isDraftValid && !isDraftEmpty}
          className="flex size-4 shrink-0 items-center justify-center rounded-sm text-success transition-colors hover:bg-background-lighter disabled:cursor-not-allowed disabled:opacity-40 2xl:size-7"
          onClick={confirmEditing}
        >
          <CheckIcon aria-hidden="true" className="size-3.5 2xl:size-4" />
        </button>
        <button
          type="button"
          aria-label={t('cancelColorEdit')}
          className="-ml-0.2 flex size-4 shrink-0 items-center justify-center rounded-sm text-text-secondary transition-colors hover:bg-background-lighter hover:text-primary-darker 2xl:-ml-1 2xl:size-7"
          onClick={cancelEditing}
        >
          <XIcon aria-hidden="true" className="size-3.5 2xl:size-4" />
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      aria-label={t('editColor')}
      className="group flex h-9 min-w-0 items-center gap-2 rounded-sm px-1.5 text-left transition-colors hover:bg-background-lighter"
      onClick={startEditing}
    >
      <span
        aria-hidden="true"
        className="size-6 shrink-0 rounded-sm border border-background-darker shadow-sm"
        style={{ backgroundColor: value ?? fallbackColor ?? undefined }}
      />
      <span className="min-w-0 truncate font-mono text-xs font-semibold uppercase leading-5 text-text-secondary">
        {displayedLabel}
      </span>
      <PencilIcon
        aria-hidden="true"
        className="size-4 shrink-0 text-text-secondary opacity-0 transition-opacity group-hover:opacity-100"
      />
    </button>
  );
};
